import React, { useState, useEffect } from 'react';
import { getSqftRevenueAnalytics, getSqftOptimizationInsights, logActivity } from '../../services/supabaseService';

import Button from '../../components/ui/Button';

import SqftMetricsGrid from './components/SqftMetricsGrid';
import SqftRevenueChart from './components/SqftRevenueChart';
import OptimizationInsights from './components/OptimizationInsights';

const SqftRevenueEngine = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    loadSqftData();
  }, []);

  const loadSqftData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsData, insightsData] = await Promise.all([
        getSqftRevenueAnalytics(),
        getSqftOptimizationInsights()
      ]);

      setAnalytics(analyticsData);
      setInsights(insightsData);

      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'sqft_revenue',
        entityName: 'Sqft Revenue Engine',
        description: 'Viewed sqft revenue analytics'
      });
    } catch (err) {
      console.error('Sqft data load error:', err);
      setError(err?.message || 'Failed to load sqft revenue data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading sqft analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-800">Error: {error}</p>
          <Button onClick={loadSqftData} className="mt-4" variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Sqft Revenue Engine</h1>
          <p className="text-muted-foreground">Per-sqft revenue analytics and optimization insights</p>
        </div>
        <Button onClick={loadSqftData} variant="outline" iconName="RefreshCw" iconPosition="left">
          Refresh
        </Button>
      </div>

      {/* Metrics Grid */}
      <SqftMetricsGrid analytics={analytics} />

      {/* Revenue Chart */}
      <SqftRevenueChart analytics={analytics} />

      {/* Optimization Insights */}
      <OptimizationInsights insights={insights} />
    </div>
  );
};

export default SqftRevenueEngine;